import { AxiosResponse } from 'axios';
import { NonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';
import type { LedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';
import { MemberLoginRequestData, MemberLoginResponse } from '@/types';
import { RegionType } from '@/constants/auth-constants';

export interface MemberLoginServiceOptions {
    ledsService: LedsService;
    nonSecureService: NonsecureService
}

/**
 * Define service APIs consumable by the client
 * @property signal {AbortSignal}
 */
export interface MemberLoginService {
    memberLogin(options: LoginRequestOptions): Promise<AxiosResponse<MemberLoginResponse>>
}

interface LoginRequestOptions {
    signal?: AbortSignal;
    payload: MemberLoginRequestData;
    releaseTag?: string;
    legacyPortalEnvironment?: string;
    region?: RegionType;
}

export function createMemberLoginService(
    memberLoginServiceOptions: MemberLoginServiceOptions
): MemberLoginService {
    const { nonSecureService, ledsService } = memberLoginServiceOptions;
    const nonSecureServiceClient = nonSecureService.client;
    const ledsClient = ledsService.client;

    async function memberLogin(options: LoginRequestOptions): Promise<AxiosResponse<MemberLoginResponse>> {
        const { signal, payload, region, legacyPortalEnvironment } = options;

        const headers = await ledsService.createAuthorizationHeaders();
        delete headers['Authorization'];
        region && (headers['X-Region'] = region);
        legacyPortalEnvironment && (headers['X-Environment'] = legacyPortalEnvironment);

        // TODO - If we switch tag names to release versions, we'll need another way to determine LEDS URL.
        let baseUrl = ledsClient.defaults.baseURL;
        if (options?.releaseTag?.includes('unit')) {
            baseUrl = 'https://unit.api.bcbssc.com/';
        } else if (options?.releaseTag?.includes('system')) {
            baseUrl = 'https://system.api.bcbssc.com/';
        } else if (options?.releaseTag?.includes('qa')) {
            baseUrl = 'https://qa.api.bcbssc.com/';
        }

        const serviceUrl = `${baseUrl}member/authenticate/password`;
        const response = await nonSecureServiceClient.post(serviceUrl, { ...payload }, {
            headers,
            signal,
            validateStatus: status => [200, 428, 434].includes(status)
        });
        response.data = response?.data ? response.data : {};
        return response;
    }

    return {
        memberLogin,
    };
}
